import React, { useEffect, useRef, useState } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, EffectCoverflow, Autoplay } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import 'swiper/css/effect-coverflow';
import './css/portfolio.css';
import { useLanguage } from '../contexts/LanguageContext';
import { getImageUrl, getApiUrl } from '../utils/config';

const PortfolioSlider = () => {
  const { t, language } = useLanguage();
  const swiperRef = useRef(null);
  const [selectedProject, setSelectedProject] = useState(null);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  const openLightbox = (project) => {
    setSelectedProject(project);
  };

  const closeLightbox = () => {
    setSelectedProject(null);
  };

  useEffect(() => {
    loadProjects();
  }, [language]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        closeLightbox();
      }
    };

    if (selectedProject) {
      document.body.style.overflow = 'hidden';
      window.addEventListener('keydown', handleKeyDown);
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [selectedProject]);

  const loadProjects = async () => {
    try {
      setLoading(true);
      const response = await fetch(getApiUrl('portfolio'));
      const data = await response.json();
      if (data.success && data.data) {
        // Transform API data to match component structure
        const transformedProjects = data.data.map(project => ({
          id: project.id,
          image: getImageUrl(project.image_url),
          tag: language === 'ar' ? (project.tag_ar || t('portfolio.projectTag')) : (project.tag_en || t('portfolio.projectTag')),
          title: language === 'ar' ? (project.title_ar || t('portfolio.projectTitle')) : (project.title_en || t('portfolio.projectTitle'))
        }));
        setProjects(transformedProjects);
        console.log('Portfolio projects loaded:', transformedProjects.length);
      } else {
        console.warn('No projects data received');
        setProjects([]);
      }
    } catch (error) {
      console.error('Error loading projects:', error);
      setProjects([]);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <section className="portfolio-section">
        <div className="container">
          <div style={{ textAlign: 'center', padding: '60px 20px', color: '#666' }}>
            <p>{language === 'ar' ? 'جاري تحميل المشاريع...' : 'Loading projects...'}</p>
          </div>
        </div>
      </section>
    );
  }

  if (projects.length === 0) {
    return (
      <section className="portfolio-section">
        <div className="container">
          <div className="section-header">
            <div className="section-tag">
              <span className="line"></span>
              <span className="tag-text">{t('portfolio.tag')}</span>
              <span className="line"></span>
            </div>
            <h1 className="section-title">
              {t('portfolio.title')}
            </h1>
          </div>
          <div style={{ textAlign: 'center', padding: '60px 20px', color: '#666' }}>
            <p>{language === 'ar' ? 'لا توجد مشاريع متاحة' : 'No projects available'}</p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="portfolio-section">
      <div className="container">
        <div className="section-header">
          <div className="section-tag">
            <span className="line"></span>
            <span className="tag-text">{t('portfolio.tag')}</span>
            <span className="line"></span>
          </div>
          <h1 className="section-title">
          {t('portfolio.title')}
          </h1>
        </div>

        <div className="slider-wrapper">
          <Swiper
            ref={swiperRef}
            modules={[Navigation, Pagination, EffectCoverflow, Autoplay]}
            effect="coverflow"
            grabCursor={true}
            centeredSlides={true}
            slidesPerView="auto"
            autoplay={{
              delay: 4000,
              disableOnInteraction: false,
              pauseOnMouseEnter: true,
            }}
            coverflowEffect={{
              rotate: 0,
              stretch: 0,
              depth: 100,
              modifier: 2.5,
              slideShadows: false,
            }}
            navigation={{
              nextEl: '.swiper-button-next-custom',
              prevEl: '.swiper-button-prev-custom',
            }}
            pagination={{
              el: '.swiper-pagination-custom',
              clickable: true,
              renderBullet: (index, className) => {
                return `<span class="${className}"></span>`;
              },
            }}
            className="portfolio-swiper"
            key={projects.length} // Force re-render when projects change
          >
            {projects.map((project) => (
              <SwiperSlide key={project.id} className="portfolio-slide">
                <div
                  className="slide-card"
                  onClick={() => openLightbox(project)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      openLightbox(project);
                    }
                  }}
                >
                  <img src={project.image} alt={project.title} className="slide-image" />
                  <div className="slide-overlay">
                    <span className="project-tag">{project.tag}</span>
                    <h3 className="project-title">{project.title}</h3>
                  </div>
                </div>
              </SwiperSlide>
            ))}
          </Swiper>

          <div className="slider-controls">
            <span className="control-line left-line"></span>
            <button className="swiper-button-prev-custom">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M12 16L6 10L12 4" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
            <div className="swiper-pagination-custom"></div>
            <button className="swiper-button-next-custom">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M8 4L14 10L8 16" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
            <span className="control-line right-line"></span>
          </div>
        </div>
      </div>
      {selectedProject && (
        <div className="portfolio-lightbox" onClick={closeLightbox} role="dialog" aria-modal="true">
          <div className="lightbox-content" onClick={(event) => event.stopPropagation()}>
            <button className="lightbox-close" onClick={closeLightbox} aria-label="Close">
              <span></span>
              <span></span>
            </button>
            <div className="lightbox-image-wrapper">
              <img src={selectedProject.image} alt={selectedProject.title} />
            </div>
            <div className="lightbox-details">
              <span className="lightbox-tag">{selectedProject.tag}</span>
              <h3 className="lightbox-title">{selectedProject.title}</h3>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default PortfolioSlider;